import React, { useRef, useState, useEffect } from 'react';
import { UserCheck, Layers, Headphones, Network } from 'lucide-react';

const advantages = [
  {
    Icon: UserCheck,
    title: 'Osobní přístup',
    description:
      'Ke každému klientovi přistupujeme individuálně. Žádné šablony — každé řešení šijeme přesně na míru vašemu byznysu.',
    accent: 'from-red-500 to-red-600',
  },
  {
    Icon: Layers,
    title: 'Komplexní řešení',
    description:
      'Pokrýváme celé spektrum IT & telco potřeb pod jednou střechou. Jeden partner, jedno číslo, nulový chaos.',
    accent: 'from-indigo-500 to-indigo-600',
  },
  {
    Icon: Headphones,
    title: 'Technická podpora',
    description:
      'Náš tým je k dispozici pro technické konzultace, asistenci při přechodu i průběžnou správu vašich služeb.',
    accent: 'from-green-500 to-green-600',
  },
  {
    Icon: Network,
    title: 'Špičková síť partnerů',
    description:
      'Spolupracujeme s předními operátory a cloud providery. Vyjednáme pro vás podmínky, které samostatně nezískáte.',
    accent: 'from-orange-500 to-orange-600',
  },
];

const WhyUs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="why-us"
      className="py-16 md:py-24 bg-white relative overflow-hidden"
    >
      {/* Subtle background accent */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 via-red-400 to-transparent" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block text-sm font-semibold uppercase tracking-widest text-red-600 mb-3">
            Proč Vyjednáme
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Váš strategický partner
            <br />
            <span className="bg-gradient-to-r from-red-600 to-red-500 bg-clip-text text-transparent">
              pro IT &amp; Telco
            </span>
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto mb-5" />
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Nejsme jen další broker. Jsme partner, který stojí na vaší straně a vyjednává
            nejlepší podmínky napříč celým trhem.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-6xl mx-auto">
          {advantages.map(({ Icon, title, description, accent }, i) => (
            <div
              key={title}
              className={`group relative bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${150 + i * 100}ms` }}
            >
              {/* Top colour bar */}
              <div className={`absolute top-0 left-6 right-6 h-0.5 rounded-b-full bg-gradient-to-r ${accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

              <div
                className={`w-12 h-12 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center mb-5 shadow-md group-hover:scale-110 transition-transform duration-300`}
              >
                <Icon size={22} className="text-white" />
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
            </div>
          ))}
        </div>

        {/* Stats strip */}
        <div
          className={`mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {[
            { value: '500+', label: 'spokojených klientů' },
            { value: '30–40 %', label: 'průměrná úspora nákladů' },
            { value: '24 h', label: 'doba přípravy nabídky' },
            { value: '10+', label: 'let zkušeností v oboru' },
          ].map(({ value, label }) => (
            <div key={label} className="text-center">
              <p className="text-3xl font-extrabold text-red-600 leading-none mb-1">{value}</p>
              <p className="text-sm text-gray-500">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
